import React, { useCallback, useEffect, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import Actions from 'Actions';
import checkoutUtils from 'utils/Checkout';
import GooglePay from 'utils/GooglePay';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';
import LanguageLocale from 'utils/LanguageLocale';
import store from 'Store';

import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';

import { buttons } from 'style/config';

const GooglePayButton = ({
    isGooglePayPayment, isBopis, isSdd, transactionAmount, css, ...rest
}) => {
    const containerRef = useRef(null);

    const handleClick = useCallback(async () => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                linkName: 'basket:payment:googlepay',
                actionInfo: 'basket:payment:googlepay',
                eventStrings: [anaConsts.Event.EVENT_71]
            }
        });

        try {
            const result = await GooglePay.startCheckout({
                transactionAmount,
                transactionCurrencyCode: LanguageLocale.getCountryISOCurrency(),
                isBopis,
                isSdd
            });

            if (result?.orderResponse) {
                checkoutUtils.placeOrderSuccess(result.orderResponse);
            }
        } catch (err) {
            // CANCEL — silent return. GooglePay.requestPaymentData normalizes
            // statusCode 'CANCELED' to error.reason 'incompleteCheckout'.
            if (err?.reason === GooglePay.GOOGLE_PAY_ERROR_MESSAGES.incompleteCheckout) {
                return;
            }

            Sephora.logger.error('[GooglePay] startCheckout failed', err);

            const getText = LanguageLocale.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    isHtml: true,
                    showCloseButton: true,
                    title: getText('googlePayErrorTitle'),
                    message: getText('googlePayErrorMessage'),
                    buttonText: getText('googlePayErrorOkButton')
                })
            );
        }
    }, [transactionAmount, isBopis, isSdd]);

    useEffect(() => {
        if (isGooglePayPayment === GooglePay.TYPES.HIDDEN || !containerRef.current) {
            return undefined;
        }

        let buttonNode;

        try {
            buttonNode = GooglePay.getPaymentsClient().createButton({
                onClick: handleClick,
                buttonType: 'plain',
                buttonColor: 'black',
                buttonSizeMode: 'fill',
                buttonRadius: buttons.HEIGHT / 2
            });
            containerRef.current.appendChild(buttonNode);
        } catch (err) {
            Sephora.logger.error('[GooglePay] createButton failed', err);
        }

        return () => {
            if (buttonNode && buttonNode.parentNode === containerRef.current) {
                containerRef.current.removeChild(buttonNode);
            }
        };
    }, [isGooglePayPayment, handleClick]);

    if (isGooglePayPayment === GooglePay.TYPES.HIDDEN) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            data-at={Sephora.debug.dataAt('basket_googlepay_btn')}
            css={[containerStyle, css]}
            {...rest}
        />
    );
};

const containerStyle = {
    display: 'flex',
    width: '100%',
    height: buttons.HEIGHT,
    // The SDK-injected child is a flex item whose default min-width: auto
    // sets the parent to its largest width
    minWidth: 0,
    '& .gpay-button-fill': {
        width: '100%',
        height: buttons.HEIGHT,
        minHeight: buttons.HEIGHT,
        minWidth: 0
    }
};

export default wrapFunctionalComponent(GooglePayButton, 'GooglePayButton');
